import type { PeerInfo } from '../../server/protocol';

export interface FollowInviteActions {
  onAccept(inviter: PeerInfo): void;
  /** Auch bei Esc/Backdrop/Timeout — die App entscheidet, ob noch geantwortet wird. */
  onDecline(inviter: PeerInfo | null): void;
}

const INVITE_TTL_MS = 30_000;

/**
 * Popup für eine erhaltene Folge-Einladung: „X lädt dich ein" mit
 * Folgen/Ablehnen. Verfällt automatisch, Esc/Backdrop gelten als Ablehnen.
 * Nutzt das bestehende Overlay-Muster (ShareOverlay).
 */
export class FollowInviteOverlay {
  el: HTMLElement;
  private nameEl: HTMLSpanElement;
  private dotEl: HTMLSpanElement;
  private timer: number | null = null;
  private inviterValue: PeerInfo | null = null;

  get isVisible(): boolean {
    return !this.el.classList.contains('hidden');
  }

  /** Wer hat eingeladen (null = kein Popup aktiv). */
  get inviter(): PeerInfo | null {
    return this.inviterValue;
  }

  constructor(root: HTMLElement, private actions: FollowInviteActions) {
    this.el = document.createElement('div');
    this.el.className = 'overlay hidden';
    const box = document.createElement('div');
    box.className = 'overlay-box invite-box';

    const h2 = document.createElement('h2');
    h2.textContent = 'Ansicht folgen?';
    box.appendChild(h2);

    const row = document.createElement('p');
    row.className = 'invite-peer-line';
    this.dotEl = document.createElement('span');
    this.dotEl.className = 'invite-peer-dot';
    this.nameEl = document.createElement('span');
    this.nameEl.className = 'invite-peer-name';
    row.append(this.dotEl, this.nameEl);
    box.appendChild(row);

    const note = document.createElement('p');
    note.className = 'panel-note';
    note.textContent = 'Deine Kamera übernimmt live, was diese Person sieht. Eigene Kamerabewegung beendet das Folgen.';
    box.appendChild(note);

    const btnRow = document.createElement('div');
    btnRow.className = 'invite-actions';
    const declineBtn = document.createElement('button');
    declineBtn.className = 'top-btn';
    declineBtn.textContent = 'Ablehnen';
    declineBtn.onclick = () => this.decline();
    const acceptBtn = document.createElement('button');
    acceptBtn.className = 'top-btn invite-accept';
    acceptBtn.textContent = 'Folgen';
    acceptBtn.onclick = () => {
      const inviter = this.inviterValue;
      if (!inviter) return;
      this.dismiss();
      this.actions.onAccept(inviter);
    };
    btnRow.append(declineBtn, acceptBtn);
    box.appendChild(btnRow);

    this.el.appendChild(box);
    // Backdrop-Klick = Ablehnen
    this.el.addEventListener('click', (e) => {
      if (e.target === this.el) this.decline();
    });
    // Esc zuerst hier abfangen, bevor andere Handler reagieren
    this.keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.isVisible) {
        e.preventDefault();
        e.stopPropagation();
        this.decline();
      }
    };
    root.appendChild(this.el);
  }

  private keyHandler: (e: KeyboardEvent) => void;

  show(inviter: PeerInfo): void {
    this.inviterValue = inviter;
    this.nameEl.textContent = `„${inviter.name}" lädt dich ein, der Ansicht zu folgen.`;
    this.dotEl.style.setProperty('--peer-color', inviter.color);
    this.dotEl.textContent = inviter.name.charAt(0).toUpperCase();
    this.el.classList.remove('hidden');
    document.addEventListener('keydown', this.keyHandler, true);
    this.armTimeout();
  }

  /** Popup schließen, ohne zu antworten (z. B. Einladender ist gegangen). */
  dismiss(): void {
    this.clearTimeout();
    this.inviterValue = null;
    this.el.classList.add('hidden');
    document.removeEventListener('keydown', this.keyHandler, true);
  }

  private decline(): void {
    const inviter = this.inviterValue;
    this.dismiss();
    this.actions.onDecline(inviter);
  }

  private armTimeout(): void {
    this.clearTimeout();
    this.timer = window.setTimeout(() => {
      this.timer = null;
      if (this.isVisible) this.decline();
    }, INVITE_TTL_MS);
  }

  private clearTimeout(): void {
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
